import { T } from '@tolgee/react';
import { ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { MUTED_ITALIC, STRIP_CURRICULUM, STRIP_FREE } from './pricingTokens';

/** The lighter parenthetical that trails some feature names in the design. */
const FeatureAside = styled.span`
  font-weight: 700;
  color: ${MUTED_ITALIC};
`;

export type PlanId = 'free' | 'premium' | 'schoolDistrict' | 'curriculumAlignment';

/** Every plan column, in the order they appear left to right. */
export const ALL_PLAN_IDS: PlanId[] = [
  'free',
  'premium',
  'schoolDistrict',
  'curriculumAlignment',
];

/**
 * The individual Premium plan is feature-flagged and off for the US, so most
 * visitors see three plans. The mock this page is built from has no Premium
 * card or column at all — it's grafted in here to preserve that existing,
 * already-shipped plan rather than dropping it during the redesign.
 */
export const planIdsFor = (showIndividualPremium: boolean): PlanId[] =>
  ALL_PLAN_IDS.filter(id => id !== 'premium' || showIndividualPremium);

export type PlanCell =
  | { kind: 'check' }
  | { kind: 'none' }
  | { kind: 'text'; node: ReactNode; emphasis?: boolean };

export const check = (): PlanCell => ({ kind: 'check' });
export const none = (): PlanCell => ({ kind: 'none' });
export const text = (node: ReactNode, emphasis = false): PlanCell => ({
  kind: 'text',
  node,
  emphasis,
});

export type PlanCtaVariant = 'solid' | 'outline';

export type PlanCta = {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  variant: PlanCtaVariant;
};

export type PlanColumn = {
  id: PlanId;
  eyebrow: ReactNode;
  title: ReactNode;
  /** "$0", "Custom" — static for Free/School/Curriculum. Premium sets this
   *  dynamically to its billing-toggle component instead of a plain label. */
  price?: ReactNode;
  /** "/ month" — Free only. */
  priceUnit?: ReactNode;
  tagline: ReactNode;
  highlights: ReactNode[];
  stripColor: string;
  /** School & District's emphasis border + shadow. */
  emphasized?: boolean;
  /** "Most chosen" — School & District only. */
  badge?: ReactNode;
  /**
   * Omitted when the visitor should be offered nothing at all — e.g. the Free
   * column for someone already on a school licence.
   */
  cta?: PlanCta;
  /** Rendered in place of the CTA, e.g. "Your current plan". */
  ctaReplacement?: ReactNode;
  /** Comparison-table column heading, and the per-value label on mobile. */
  shortLabel: ReactNode;
};

export type FeatureRow = {
  id: string;
  name: ReactNode;
  description?: ReactNode;
  cells: Record<PlanId, PlanCell>;
};

export type FeatureGroup = {
  id: string;
  heading: ReactNode;
  rows: FeatureRow[];
};

/* ─── Static parts of the plan columns ────────────────────────────────────── */

type StaticPlanColumn = Pick<
  PlanColumn,
  | 'id'
  | 'eyebrow'
  | 'title'
  | 'tagline'
  | 'highlights'
  | 'stripColor'
  | 'emphasized'
  | 'badge'
  | 'shortLabel'
> & {
  /** Free/School/Curriculum have a static price; Premium builds its own. */
  price?: ReactNode;
  priceUnit?: ReactNode;
};

export const staticPlanColumns: Record<PlanId, StaticPlanColumn> = {
  free: {
    id: 'free',
    eyebrow: <T keyName="Pricing.Plan.Free.Eyebrow" defaultValue="For every teacher" />,
    title: <T keyName="Pricing.Plan.Free.Title" defaultValue="Free" />,
    price: <T keyName="Pricing.Plan.Free.Price" defaultValue="$0" />,
    priceUnit: <T keyName="Pricing.Plan.Free.PriceUnit" defaultValue="/ month" />,
    tagline: (
      <T keyName="Pricing.Plan.Free.Tagline" defaultValue="No card, no procurement." />
    ),
    highlights: [
      <T
        key="0"
        keyName="Pricing.Plan.Free.Highlight1"
        defaultValue="Weekly writing sessions with instant Glow & Grow feedback"
      />,
      <T
        key="1"
        keyName="Pricing.Plan.Free.Highlight2"
        defaultValue="Unlimited student writers, no student accounts"
      />,
      <T
        key="2"
        keyName="Pricing.Plan.Free.Highlight3"
        defaultValue="National and state standards alignment"
      />,
      <T
        key="3"
        keyName="Pricing.Plan.Free.Highlight4"
        defaultValue="Select state test prep content"
      />,
    ],
    stripColor: STRIP_FREE,
    shortLabel: <T keyName="Pricing.Plan.Free.Short" defaultValue="Free" />,
  },
  premium: {
    id: 'premium',
    eyebrow: (
      <T keyName="Pricing.Plan.Premium.Eyebrow" defaultValue="For individual teachers" />
    ),
    title: <T keyName="Pricing.Plan.Premium.Title" defaultValue="Premium" />,
    // A general caption for the comparison table's header, which has no
    // awareness of the billing-frequency toggle the plan card uses instead
    // (PlanOverviewSection overrides `price` with the live toggle there).
    price: (
      <T keyName="Pricing.Plan.Premium.PriceCaption" defaultValue="From $24/month" />
    ),
    tagline: (
      <T
        keyName="Pricing.Plan.Premium.Tagline"
        defaultValue="For teachers upgrading beyond free."
      />
    ),
    highlights: [
      <T
        key="0"
        keyName="Pricing.Plan.Premium.Highlight1"
        defaultValue="Unlimited writing practice for ELA, Social Studies and Science (K-12)"
      />,
      <T
        key="1"
        keyName="Pricing.Plan.Premium.Highlight2"
        defaultValue="Unlimited AI feedback, lesson generation and translations"
      />,
      <T
        key="2"
        keyName="Pricing.Plan.Premium.Highlight3"
        defaultValue="Text-to-speech, authentic writing controls and custom rubrics"
      />,
      <T
        key="3"
        keyName="Pricing.Plan.Premium.SupportHighlight"
        defaultValue="Basic support"
      />,
    ],
    stripColor: theme.colors.paleBeige,
    shortLabel: <T keyName="Pricing.Plan.Premium.Short" defaultValue="Premium" />,
  },
  schoolDistrict: {
    id: 'schoolDistrict',
    eyebrow: (
      <T keyName="Pricing.Plan.School.Eyebrow" defaultValue="For schools & districts" />
    ),
    title: <T keyName="Pricing.Plan.School.Title" defaultValue="School & District" />,
    price: <T keyName="Pricing.Plan.School.Price" defaultValue="Custom pricing" />,
    tagline: (
      <T
        keyName="Pricing.Plan.School.Tagline"
        defaultValue="Site license, priced by student count."
      />
    ),
    highlights: [
      <T
        key="0"
        keyName="Pricing.Plan.School.Highlight1"
        defaultValue="Unlimited writing practice for ELA, Social Studies and Science (K–12)"
      />,
      <T
        key="1"
        keyName="Pricing.Plan.School.Highlight2"
        defaultValue="Writing growth reports across every classroom and school"
      />,
      <T
        key="2"
        keyName="Pricing.Plan.School.Highlight3"
        defaultValue="SSO, implementation support and priority support"
      />,
      <T
        key="3"
        keyName="Pricing.Plan.School.Highlight4"
        defaultValue="Signed district data privacy agreements"
      />,
    ],
    stripColor: theme.colors.orange,
    emphasized: true,
    badge: <T keyName="Pricing.Plan.School.Badge" defaultValue="Most chosen" />,
    shortLabel: (
      <T keyName="Pricing.Plan.School.Short" defaultValue="School & District" />
    ),
  },
  curriculumAlignment: {
    id: 'curriculumAlignment',
    eyebrow: <T keyName="Pricing.Plan.Curriculum.Eyebrow" defaultValue="For districts" />,
    title: (
      <T
        keyName="Pricing.Plan.Curriculum.Title"
        defaultValue="ELA Curriculum Alignment"
      />
    ),
    price: <T keyName="Pricing.Plan.Curriculum.Price" defaultValue="Custom pricing" />,
    tagline: (
      <T
        keyName="Pricing.Plan.Curriculum.Tagline"
        defaultValue="Built onto your adopted curriculum."
      />
    ),
    highlights: [
      <T
        key="0"
        keyName="Pricing.Plan.Curriculum.Highlight1"
        defaultValue="Everything in School & District"
      />,
      <T
        key="1"
        keyName="Pricing.Plan.Curriculum.Highlight2"
        defaultValue="Writing tasks mapped to HMH, Savvas, Bluebonnet and more"
      />,
      <T
        key="2"
        keyName="Pricing.Plan.Curriculum.Highlight3"
        defaultValue="Sequenced to your pacing calendar, ready every week"
      />,
      <T
        key="3"
        keyName="Pricing.Plan.Curriculum.Highlight4"
        defaultValue="White-glove setup, PD and a dedicated partner"
      />,
    ],
    stripColor: STRIP_CURRICULUM,
    shortLabel: (
      <T
        keyName="Pricing.Plan.Curriculum.ShortEla"
        defaultValue="ELA Curriculum Alignment"
      />
    ),
  },
};

/* ─── Full feature comparison ─────────────────────────────────────────────── */

const unlimited = (): PlanCell =>
  text(<T keyName="Pricing.Compare.Unlimited" defaultValue="Unlimited" />, true);

const allTestPrep = (): PlanCell =>
  text(
    <T
      keyName="Pricing.Compare.FullTestPrepAccess"
      defaultValue="Full access to all test prep libraries"
    />,
    true,
  );

const noneText = (): PlanCell =>
  text(<T keyName="Pricing.Compare.None" defaultValue="None" />);

const upTo5000 = (): PlanCell =>
  text(
    <T keyName="Pricing.Compare.UpTo5000" defaultValue="Up to 5,000 characters" />,
    true,
  );

const realTimeModeration = (): PlanCell =>
  text(
    <T
      keyName="Pricing.Compare.RealTimeModeration"
      defaultValue="Real-time moderation"
    />,
    true,
  );

const districtOnly = (): PlanCell =>
  text(<T keyName="Pricing.Compare.DistrictOnly" defaultValue="District only" />, true);

const allPrivacyLaws = (): PlanCell =>
  text(
    <T
      keyName="Pricing.Compare.AllPrivacyLaws"
      defaultValue="All federal and state privacy laws"
    />,
  );

export const featureGroups: FeatureGroup[] = [
  {
    id: 'content',
    heading: <T keyName="Pricing.Compare.Group.Content" defaultValue="Content" />,
    rows: [
      {
        id: 'unlimited-writers',
        name: (
          <T
            keyName="Pricing.Compare.UnlimitedWriters"
            defaultValue="Unlimited student writers"
          />
        ),
        cells: {
          free: check(),
          premium: check(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'writing-sessions',
        name: (
          <T keyName="Pricing.Compare.WritingSessions" defaultValue="Writing sessions" />
        ),
        cells: {
          free: text(
            <T
              keyName="Pricing.Compare.WritingSessions.Free"
              defaultValue="Weekly sessions, renews every week"
            />,
          ),
          premium: unlimited(),
          schoolDistrict: unlimited(),
          curriculumAlignment: unlimited(),
        },
      },
      {
        id: 'standards-alignment',
        name: (
          <T
            keyName="Pricing.Compare.StandardsAlignment"
            defaultValue="Standards alignment (national and state)"
          />
        ),
        cells: {
          free: check(),
          premium: check(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'state-test-practice',
        name: (
          <T
            keyName="Pricing.Compare.StateTestPractice"
            defaultValue="State test-aligned writing practice (3rd–12th)"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.StateTestPractice.Desc"
            defaultValue="Constructed-response practice with feedback aligned to your state test rubrics."
          />
        ),
        cells: {
          free: text(
            <T
              keyName="Pricing.Compare.StateTestPractice.FreeTrial"
              defaultValue="Try select test prep content for free"
            />,
          ),
          // Individual Premium has never included the test prep libraries.
          premium: none(),
          schoolDistrict: allTestPrep(),
          curriculumAlignment: allTestPrep(),
        },
      },
      {
        id: 'ela-writing-practice',
        name: (
          <T
            keyName="Pricing.Compare.ElaWriting"
            defaultValue="ELA writing practice (K–12)"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.ElaWriting.Desc"
            defaultValue="Students write, get specific feedback in seconds, discuss, and revise."
          />
        ),
        cells: {
          free: text(
            <T keyName="Pricing.Compare.LimitedUsage2" defaultValue="Limited usage" />,
          ),
          premium: unlimited(),
          schoolDistrict: unlimited(),
          curriculumAlignment: unlimited(),
        },
      },
      {
        id: 'science-social-writing',
        name: (
          <T
            keyName="Pricing.Compare.ScienceSocialWriting"
            defaultValue="Science & Social Studies writing practice (3rd–12th)"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.ScienceSocialWriting.Desc"
            defaultValue="Claim, evidence and reasoning practice across the curriculum."
          />
        ),
        cells: {
          free: text(
            <T keyName="Pricing.Compare.LimitedUsage2" defaultValue="Limited usage" />,
          ),
          premium: unlimited(),
          schoolDistrict: unlimited(),
          curriculumAlignment: unlimited(),
        },
      },
      {
        id: 'bilingual-practice',
        name: (
          <T
            keyName="Pricing.Compare.Bilingual"
            defaultValue="Bilingual (ESL/ELL) writing practice"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.Bilingual.Desc"
            defaultValue="TELPAS, ELPAC and WIDA ACCESS."
          />
        ),
        cells: {
          free: none(),
          // Individual Premium doesn't include the bilingual test-standard
          // (TELPAS/ELPAC/WIDA) practice this row describes.
          premium: none(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'adopted-curriculum-mapping',
        name: (
          <T
            keyName="Pricing.Compare.AdoptedCurriculum"
            defaultValue="Writing tasks mapped to your adopted curriculum"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.AdoptedCurriculum.Desc"
            defaultValue="Practice for every unit, built around the texts your district already uses."
          />
        ),
        cells: {
          free: none(),
          premium: none(),
          schoolDistrict: none(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'pacing-alignment',
        name: (
          <T
            keyName="Pricing.Compare.PacingAlignment"
            defaultValue="Pacing and scope & sequence alignment"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.PacingAlignment.Desc"
            defaultValue="Sequenced to your calendar, so teachers open the week and it's ready."
          />
        ),
        cells: {
          free: none(),
          premium: none(),
          schoolDistrict: none(),
          curriculumAlignment: check(),
        },
      },
    ],
  },
  {
    id: 'classroom-controls',
    heading: (
      <T
        keyName="Pricing.Compare.Group.ClassroomControls"
        defaultValue="Classroom controls & reporting"
      />
    ),
    rows: [
      {
        id: 'text-to-speech',
        name: (
          <T
            keyName="Pricing.Compare.TextToSpeech"
            defaultValue="Text to speech in read-aloud passages"
          />
        ),
        cells: {
          free: none(),
          premium: check(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'authentic-writing-controls',
        name: (
          <T
            keyName="Pricing.Compare.AuthenticWriting"
            defaultValue="Authentic writing controls"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.AuthenticWriting.Desc"
            defaultValue="Disable pasting and third-party assistance, so you see what students can actually do."
          />
        ),
        cells: {
          free: none(),
          // Not part of the individual Premium plan.
          premium: none(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'own-rubric',
        name: (
          <T
            keyName="Pricing.Compare.OwnRubric"
            defaultValue="Score writing against your own rubric"
          />
        ),
        cells: {
          free: none(),
          premium: check(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'response-length',
        name: (
          <T
            keyName="Pricing.Compare.ResponseLength"
            defaultValue="Student written responses"
          />
        ),
        cells: {
          free: text(
            <T
              keyName="Pricing.Compare.ResponseLength.Free"
              defaultValue="Up to 1,000 characters"
            />,
          ),
          premium: text(
            <T
              keyName="Pricing.Compare.ResponseLength.Premium"
              defaultValue="Up to 2,300 characters"
            />,
            true,
          ),
          schoolDistrict: upTo5000(),
          curriculumAlignment: upTo5000(),
        },
      },
      {
        id: 'content-translations',
        name: (
          <T keyName="Pricing.Compare.Translations" defaultValue="Content translations" />
        ),
        cells: {
          free: text(
            <T keyName="Pricing.Compare.Translations.Free" defaultValue="Limited" />,
          ),
          premium: unlimited(),
          schoolDistrict: unlimited(),
          curriculumAlignment: unlimited(),
        },
      },
      {
        id: 'pdf-download',
        name: (
          <T
            keyName="Pricing.Compare.PdfDownload"
            defaultValue="Download lessons as PDF and share results"
          />
        ),
        description: (
          <T
            keyName="Pricing.Compare.PdfDownload.Desc"
            defaultValue="For absent students, offline access and paper backup."
          />
        ),
        cells: {
          free: none(),
          premium: check(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'writing-reports',
        name: (
          <T
            keyName="Pricing.Compare.WritingReports"
            defaultValue="Writing reports and student growth insights"
          />
        ),
        cells: {
          free: none(),
          // Not part of the individual Premium plan.
          premium: none(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'shared-folders',
        name: (
          <T
            keyName="Pricing.Compare.SharedFolders"
            defaultValue="Shared lesson folders across schools & district"
          />
        ),
        cells: {
          free: none(),
          premium: none(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
    ],
  },
  {
    id: 'implementation',
    heading: (
      <T
        keyName="Pricing.Compare.Group.Implementation"
        defaultValue="Implementation & support"
      />
    ),
    rows: [
      {
        id: 'customer-support',
        name: (
          <T keyName="Pricing.Compare.CustomerSupport" defaultValue="Customer support" />
        ),
        cells: {
          free: text(
            <T
              keyName="Pricing.Compare.CustomerSupport.Free"
              defaultValue="Basic support"
            />,
          ),
          // Individual Premium gets the same support tier as Free — matches
          // the "Basic support" highlight on Premium's own card.
          premium: text(
            <T
              keyName="Pricing.Compare.CustomerSupport.Free"
              defaultValue="Basic support"
            />,
          ),
          schoolDistrict: text(
            <T
              keyName="Pricing.Compare.CustomerSupport.School"
              defaultValue="Priority support"
            />,
            true,
          ),
          curriculumAlignment: text(
            <T
              keyName="Pricing.Compare.CustomerSupport.Curriculum"
              defaultValue="Dedicated partner"
            />,
            true,
          ),
        },
      },
      {
        id: 'sso',
        name: (
          <>
            <T keyName="Pricing.Compare.Sso" defaultValue="SSO" />{' '}
            <FeatureAside>
              <T
                keyName="Pricing.Compare.Sso.Aside"
                defaultValue="(ClassLink, Clever, Google)"
              />
            </FeatureAside>
          </>
        ),
        cells: {
          free: none(),
          premium: none(),
          schoolDistrict: districtOnly(),
          curriculumAlignment: districtOnly(),
        },
      },
      {
        id: 'implementation-support',
        name: (
          <T
            keyName="Pricing.Compare.ImplementationSupport"
            defaultValue="Implementation support"
          />
        ),
        cells: {
          free: none(),
          premium: none(),
          schoolDistrict: check(),
          curriculumAlignment: text(
            <T
              keyName="Pricing.Compare.ImplementationSupport.Curriculum"
              defaultValue="White-glove setup + PD"
            />,
            true,
          ),
        },
      },
    ],
  },
  {
    id: 'privacy',
    heading: (
      <T
        keyName="Pricing.Compare.Group.Privacy"
        defaultValue="Data privacy & AI safety"
      />
    ),
    rows: [
      {
        id: 'compliance',
        name: (
          <T
            keyName="Pricing.Compare.CompliantWith"
            defaultValue="Compliant with FERPA, COPPA, SOPPA, GDPR"
          />
        ),
        cells: {
          free: allPrivacyLaws(),
          premium: allPrivacyLaws(),
          schoolDistrict: allPrivacyLaws(),
          curriculumAlignment: allPrivacyLaws(),
        },
      },
      {
        id: 'ai-safety',
        name: <T keyName="Pricing.Compare.AiSafety" defaultValue="AI safety" />,
        description: (
          <T
            keyName="Pricing.Compare.AiSafety.Desc"
            defaultValue="Any AI is teacher-controlled. No AI chatbots for students."
          />
        ),
        cells: {
          free: check(),
          premium: check(),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
      {
        id: 'ai-training',
        name: (
          <T
            keyName="Pricing.Compare.AiTraining"
            defaultValue="Student or teacher data used to train AI models"
          />
        ),
        cells: {
          free: noneText(),
          premium: noneText(),
          schoolDistrict: noneText(),
          curriculumAlignment: noneText(),
        },
      },
      {
        id: 'teacher-ai-control',
        name: (
          <T
            keyName="Pricing.Compare.TeacherControlsAiOutput"
            defaultValue="Teacher can control and review all AI output before students see it"
          />
        ),
        cells: {
          free: check(),
          premium: check(),
          schoolDistrict: realTimeModeration(),
          curriculumAlignment: realTimeModeration(),
        },
      },
      {
        id: 'dpa',
        name: <T keyName="Pricing.Compare.Dpa" defaultValue="Data privacy agreements" />,
        cells: {
          free: text(
            <T
              keyName="Pricing.Compare.Dpa.Free"
              defaultValue="Standard privacy policy"
            />,
          ),
          premium: text(
            <T
              keyName="Pricing.Compare.Dpa.Free"
              defaultValue="Standard privacy policy"
            />,
          ),
          schoolDistrict: check(),
          curriculumAlignment: check(),
        },
      },
    ],
  },
];
